import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { formatQuantity, formatUnitDisplay, toCanonicalUnit, formatQuantityNumber } from '../utils/unitDisplay';
import {Shield,  Bell,  Package,  ChevronDown,  Layers,  Fuel,  Droplets,  Trash2,  AlertTriangle,  AlertCircle,  FileCheck,  ShoppingCart,  CheckCircle2,  Search,  ShieldAlert,  ArrowRight,  ChefHat,  HelpCircle,  Scale,  Eye,  IndianRupee,  ChevronRight, ArrowLeft, Sparkles, RefreshCw,} from 'lucide-react';
import { motion, useInView } from 'framer-motion';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import './Leakage.css';

// Cache ingredient responses across remounts to avoid duplicate requests in React Strict Mode.
const leakageIngredientsCache = new Map();
const leakageIngredientsInFlight = new Map();
const WEEKLY_AUDIT_DISABLED_CODE = 'WEEKLY_AUDIT_DISABLED';

const getApiErrorMessage = (error, fallbackMessage) => {
  return error?.response?.data?.error
    || error?.response?.data?.message
    || error?.message
    || fallbackMessage;
};

const getTodayDateString = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const formatDateToDDMMYYYY = (dateStr) => {
  if (!dateStr || dateStr === '-') return '-';
  const parts = String(dateStr).trim().split('-');
  if (parts.length === 3) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return dateStr;
};

const isValidISODateString = (value) => /^\d{4}-\d{2}-\d{2}$/.test(String(value || '').trim());

const resolveInitialClosingDate = () => {
  const today = getTodayDateString();
  try {
    const rawBusinessId = localStorage.getItem('businessId');
    const parsedBusinessId = Number(rawBusinessId);
    if (Number.isFinite(parsedBusinessId) && parsedBusinessId > 0) {
      const scopedDate = localStorage.getItem(`weeklyAudit.selectedClosingDate_${parsedBusinessId}`);
      if (isValidISODateString(scopedDate)) {
        return String(scopedDate).trim();
      }
    }

    const globalDate = localStorage.getItem('weeklyAudit.selectedClosingDate');
    if (isValidISODateString(globalDate)) {
      return String(globalDate).trim();
    }
  } catch (storageError) {
    // non-fatal: fallback to today
  }
  return today;
};

const normalizeIngredientLabel = (value) => String(value || '')
  .toLowerCase()
  .replace(/\s+/g, ' ')
  .trim();

const normalizeAuditUnit = (rawUnit) => {
  const unit = String(rawUnit || '').trim().toLowerCase();
  if (['g', 'gm', 'gram', 'grams'].includes(unit)) return 'g';
  if (['kg', 'kilogram', 'kilograms'].includes(unit)) return 'kg';
  if (['mg', 'milligram', 'milligrams'].includes(unit)) return 'mg';
  if (['ml', 'milliliter', 'milliliters', 'millilitre', 'millilitres'].includes(unit)) return 'ml';
  if (['l', 'lt', 'liter', 'liters', 'litre', 'litres'].includes(unit)) return 'l';
  if (['pc', 'piece', 'pieces'].includes(unit)) return 'pcs';
  return unit || 'g';
};

const getAuditUnitFamily = (rawUnit) => {
  const unit = normalizeAuditUnit(rawUnit);
  if (['g', 'kg', 'mg'].includes(unit)) return 'mass';
  if (['ml', 'l'].includes(unit)) return 'volume';
  return 'count';
};

const getAuditAnchorUnitLabel = (rawUnit) => {
  const family = getAuditUnitFamily(rawUnit);
  const unit = normalizeAuditUnit(rawUnit);
  if (family === 'mass') return 'g';
  if (family === 'volume') return 'ml';
  return unit;
};

const formatAuditNumber = (value, maximumFractionDigits = 3) => (
  Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits,
  })
);

const AnimatedTsrEntry = ({ children, index }) => {
  const ref = React.useRef(null);
  const inView = useInView(ref, { amount: 0.15, triggerOnce: true });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 12 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 12 }}
      transition={{ duration: 0.25, delay: Math.min(index * 0.04, 0.12), ease: "easeOut" }}
      style={{ width: '100%' }}
    >
      {children}
    </motion.div>
  );
};

const getBaseUnitOf = (unit) => {
  const u = String(unit || '').trim().toLowerCase();
  if (['g', 'gm', 'gram', 'grams', 'kg', 'kgs', 'kilogram', 'kilograms'].includes(u)) return 'g';
  if (['ml', 'milliliter', 'millilitre', 'l', 'lt', 'liter', 'litre'].includes(u)) return 'ml';
  return 'pcs';
};

const formatQuantityShort = (rawQuantity, rawUnit) => {
  const canonicalBase = getBaseUnitOf(rawUnit);
  const scaled = formatUnitDisplay(Number(rawQuantity || 0), canonicalBase);
  return `${formatQuantityNumber(scaled.qty)} ${scaled.unit}`;
};

// Canonical, app-wide display: short symbols (g/kg/ml/L/pcs), auto-scaled.
const formatAuditAnchorQuantity = (anchorValue, rawUnit) => {
  return formatQuantityShort(anchorValue, rawUnit);
};

const formatAuditPortionQuantity = (anchorValue, rawUnit) => {
  return formatQuantityShort(anchorValue, rawUnit);
};

const formatAuditAnchorSub = (anchorValue, rawUnit, maximumFractionDigits = 3) => {
  const amount = Number(anchorValue || 0);
  const family = getAuditUnitFamily(rawUnit);
  if (!Number.isFinite(amount) || amount < 1000 || family === 'count') return '';
  return `${formatAuditNumber(amount, maximumFractionDigits)}${getAuditAnchorUnitLabel(rawUnit)}`;
};

const getAuditMismatchLabel = (rawUnit) => {
  const family = getAuditUnitFamily(rawUnit);
  if (family === 'mass') return 'Total mismatch weight';
  if (family === 'volume') return 'Total mismatch volume';
  return 'Total mismatch quantity';
};

const getAuditRateLabel = (rawUnit) => {
  const family = getAuditUnitFamily(rawUnit);
  if (family === 'mass') return 'kg';
  if (family === 'volume') return 'l';
  return `1000 ${getAuditAnchorUnitLabel(rawUnit)}`;
};

const DashboardHeader = ({ onPreviousWeek, onNextWeek, onCurrentWeek, isCurrentWeek }) => {
  return (
    <div className="dashboard-header">
      <div className="header-controls">
        <div style={{ display: 'flex', gap: '0.75rem', width: '100%', maxWidth: '600px', marginTop: '0px' }}>
          <button 
            className="week-nav-btn"
            onClick={onPreviousWeek}
            style={{
              flex: 1,
              padding: '10px 16px',
              minHeight: '48px',
              backgroundColor: 'transparent',
              border: '1px solid #ddd',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#333'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#f5f5f5';
              e.target.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.transform = 'translateY(0)';
            }}
            title="Previous closing window"
          >
            ‹ Prev Close
          </button>
          
          <button
            className="week-nav-btn"
            onClick={onCurrentWeek}
            disabled={isCurrentWeek}
            style={{
              flex: 1,
              padding: '10px 16px',
              minHeight: '48px',
              backgroundColor: isCurrentWeek ? '#333' : 'transparent',
              color: isCurrentWeek ? '#fff' : '#333',
              border: '1px solid #ddd',
              borderRadius: '8px',
              cursor: isCurrentWeek ? 'default' : 'pointer',
              fontSize: '1rem',
              fontWeight: '600',
              transition: 'all 0.2s',
              opacity: isCurrentWeek ? 0.95 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onMouseEnter={(e) => {
              if (!isCurrentWeek) {
                e.target.style.backgroundColor = '#f5f5f5';
                e.target.style.transform = 'translateY(-2px)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isCurrentWeek) {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.transform = 'translateY(0)';
              }
            }}
            title="Go to current closing window"
          >
            This Close
          </button>
          
          <button 
            className="week-nav-btn"
            onClick={onNextWeek}
            disabled={isCurrentWeek}
            style={{
              flex: 1,
              padding: '10px 16px',
              minHeight: '48px',
              backgroundColor: isCurrentWeek ? '#f5f5f5' : 'transparent',
              border: '1px solid #ddd',
              borderRadius: '8px',
              cursor: isCurrentWeek ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.2s',
              opacity: isCurrentWeek ? 0.5 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#333'
            }}
            onMouseEnter={(e) => {
              if (!isCurrentWeek) {
                e.target.style.backgroundColor = '#f5f5f5';
                e.target.style.transform = 'translateY(-2px)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isCurrentWeek) {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.transform = 'translateY(0)';
              }
            }}
            title="Next closing window"
          >
            Next Close ›
          </button>
        </div>
      </div>
    </div>
  );
};

const MasterBatch = ({
  ingredient,
  batches,
  totalKg,
  totalValue,
  weightedAvgCost,
  isHistoricalWeek = false,
  cycleMode = 'first_cycle',
  startDate = null,
  endDate = null,
  previousClosingDate = null,
  windowStartMode = null,
}) => {
  const [expandedBatch, setExpandedBatch] = useState(null);
  const safeTotalKg = Number(totalKg || 0);
  const exactBatchTotalKg = batches.reduce((sum, b) => sum + Number(b.kg || 0), 0);
  const percentBaseKg = exactBatchTotalKg > 0 ? exactBatchTotalKg : safeTotalKg;
  const isContinuationCycle = cycleMode === 'continuation_cycle';


  const formatNumber = (value, maxFraction = 2) => (
    Number(value || 0).toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: maxFraction,
    })
  );

  const formatBatchQuantity = (batch) => {
    const qty = Number(batch.originalQuantity || 0);
    const kg = Number(batch.kg || 0);
    const unit = String(batch.originalUnit || '').toLowerCase();

    if (unit === 'g' || unit === 'gram') {
      if (Math.abs(qty) >= 1000) {
        return {
          text: `${formatNumber(kg, 3)} kg`,
          showSecondaryKg: false,
        };
      }
      const cleanGrams = Math.round(qty);
      return {
        text: `${cleanGrams.toLocaleString()} g`,
        showSecondaryKg: false,
      };
    }

    return {
      text: `${formatNumber(qty, 3)} ${batch.originalUnit}`,
      showSecondaryKg: unit !== 'kg',
    };
  };

  const formatBatchRate = (batch) => {
    const perKg = Number(batch.pricePerKg || 0);
    if (String(batch.source || '') === 'carry_forward') {
      return `@ ₹${formatNumber(perKg, 2)}/kg`;
    }
    return `@ ₹${formatNumber(perKg, 2)}/${batch.originalUnit}`;
  };

  return (
    <div className="glass-card master-batch" style={{ padding: '1rem' }}>
      <div className="dot-pattern" />
      <div style={{ position: 'relative' }}>
        <div className="mb-header">
          <div style={{ flex: 1 }}>
            <h3 className="mb-title">Batch Window</h3>

          </div>
          <div className="mb-badge">
            <span>{batches.length} batches</span>
          </div>
        </div>

        {batches.length === 0 ? (
          <div style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid hsl(var(--border))',
            borderRadius: '8px',
            marginBottom: '1rem',
            color: 'hsl(var(--muted-foreground))',
            fontSize: '0.9rem',
            width: '100%',
            minHeight: '130px'
          }}>
            No batches recorded
          </div>
        ) : (
        <div className="mb-batches">
          {batches.map((batch, i) => {
            const batchKg = Number(batch.kg || 0);
            const rawPercent = percentBaseKg > 0 ? (batchKg / percentBaseKg) * 100 : 0;
            const heightPercent = Math.max(0, Math.min(100, rawPercent));
            const batchValue = batch.kg * batch.pricePerKg;
            const isExpanded = expandedBatch === i;
            const quantityDisplay = formatBatchQuantity(batch);

            return (
              <div
                key={`${batch.day}-${batch.label}-${i}`}
                className={`batch-row ${isExpanded ? 'expanded' : ''}`}
                onClick={() => setExpandedBatch(isExpanded ? null : i)}
              >
                <div className="batch-fill" style={{ width: `${heightPercent}%` }} />
                <div className="batch-hover-gradient" />
                <div className="batch-content">
                  <div className="batch-left">
                    <div>
                      <p className="batch-name">{batch.label}</p>
                    </div>
                  </div>
                  <div className="batch-right">
                    <div style={{ textAlign: 'right' }}>
                      <p className="batch-day" style={{ textAlign: 'right', margin: 0 }}>
                        {batch.day}
                      </p>
                    </div>
                    <ChevronDown className="batch-chevron" style={{ width: 16, height: 16 }} />
                  </div>
                </div>

                <div className={`batch-detail ${isExpanded ? 'open' : ''}`}>
                  <div className="batch-detail-inner">
                    <div className="batch-progress">
                      <div className="batch-progress-fill" style={{ width: `${heightPercent}%` }} />
                    </div>
                    <p className="batch-progress-text">{heightPercent.toFixed(0)}% of total batch</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        )}

        {batches.length > 0 && (
        <div className="mb-totals">
          {[
            { label: 'Total Weight', value: `${formatNumber(totalKg, 3)}kg` },
            { label: 'Total Value', value: `₹${totalValue.toLocaleString()}` },
            { label: 'Avg Cost', value: `₹${formatNumber(weightedAvgCost, 2)}`, suffix: '/kg' },
          ].map((stat) => (
            <div key={stat.label} className="mb-total-card">
              <p className="mb-total-label">{stat.label}</p>
              <div className="mb-total-value">
                {stat.value}
                {stat.suffix && <span className="mb-total-suffix">{stat.suffix}</span>}
              </div>
            </div>
          ))}
        </div>
        )}
      </div>
    </div>
  );
};

// ─── NEW: Plate-Based Leakage Table ──────────────────────────────────────────
// Primary leakage view: per-dish breakdown of plates that can be made vs sold.
const PlateLeakageTable = ({
  ingredient,
  consumedKg,
  closingStockKg,
  totalConsumedGrams,
  platesCanBeMade,
  totalPlatesSold,
  leakPlates,
  dishes,          // portionDrift dishes with platesCanBeMade, leakPlates, leakGrams
  unitSymbol,
}) => {
  const [expandedRow, setExpandedRow] = useState(null);

  const unitFamily = getAuditUnitFamily(unitSymbol);
  const leakValueHeader = unitFamily === 'mass'
    ? 'Weight Mismatch'
    : (unitFamily === 'volume' ? 'Volume Mismatch' : 'Qty Mismatch');
  const mismatchLabel = getAuditMismatchLabel(unitSymbol);

  const totalLeakGrams = dishes.reduce((s, d) => s + Number(d.leakGrams || 0), 0);
  const totalOverLeakPlates = dishes.reduce((s, d) => s + Number(d.overLeakPlates || 0), 0);
  const totalUnderLeakPlates = dishes.reduce((s, d) => s + Number(d.underLeakPlates || 0), 0);

  const hasLeakage = leakPlates > 0;

  return (
    <div className="glass-card" style={{ padding: '1rem', marginBottom: '0' }}>
      <div className="dot-pattern" />
      <div style={{ position: 'relative' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.25rem' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: 'hsl(var(--foreground))' }}>
              Plate Leakage Analysis
            </h3>

          </div>
          <div style={{
            padding: '0.25rem 0.6rem',
            borderRadius: '999px',
            background: hasLeakage ? 'hsl(var(--danger) / 0.1)' : 'hsl(var(--success) / 0.1)',
            border: `1px solid ${hasLeakage ? 'hsl(var(--danger) / 0.3)' : 'hsl(var(--success) / 0.3)'}`,
            fontSize: '12px',
            fontWeight: 700,
            color: hasLeakage ? 'hsl(var(--danger))' : 'hsl(var(--success))',
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}>
            {hasLeakage ? `${leakPlates} plates mismatch` : 'No leakage'}
          </div>
        </div>

        {/* Stock summary strip */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3,1fr)',
          gap: '0.75rem',
          marginBottom: '1.25rem',
          padding: '0.9rem',
          borderRadius: '10px',
          background: 'hsl(var(--muted) / 0.15)',
          border: '1px solid hsl(var(--border))',
        }}>
          {[
            { label: 'Consumed Stock', value: formatAuditAnchorQuantity(totalConsumedGrams, unitSymbol), color: 'hsl(var(--foreground))' },
            { label: 'Plates Can Be Made', value: platesCanBeMade, color: 'hsl(var(--primary))' },
            { label: 'Plates Sold', value: totalPlatesSold, color: 'hsl(var(--success))' },
          ].map(stat => (
            <div key={stat.label} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '1.35rem', fontWeight: 800, color: stat.color }}>{stat.value}</div>
              <div style={{ fontSize: '0.72rem', fontWeight: 600, color: 'hsl(var(--muted-foreground))', marginTop: '0.1rem' }}>{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Per-Dish Table */}
        {dishes.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.83rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid hsl(var(--border))' }}>
                  {['Menu Item', 'Qty Per Plate', 'Ideal Plates', 'Plates Sold', 'Plates Mismatch', leakValueHeader].map(h => (
                    <th key={h} style={{
                      padding: '0.45rem 0.6rem',
                      textAlign: h === 'Menu Item' ? 'left' : 'right',
                      fontSize: '0.72rem',
                      fontWeight: 600,
                      color: '#000000',
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {dishes.map((dish, i) => {
                  const dishLeak = Number(dish.leakPlates || 0);
                  const dishOverLeak = Number(dish.overLeakPlates || 0);
                  const dishUnderLeak = Number(dish.underLeakPlates || 0);
                  const rowColor = '#ffffff';
                  const leakColor = dishLeak > 0 ? 'hsl(var(--danger))' : 'hsl(var(--muted-foreground))';
                  return (
                    <tr
                      key={dish.name || i}
                      style={{
                        background: rowColor,
                        borderBottom: '1px solid hsl(var(--border) / 0.5)',
                        transition: 'background 0.15s',
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = 'hsl(var(--muted) / 0.2)'}
                      onMouseLeave={e => e.currentTarget.style.background = rowColor}
                    >
                      <td style={{ padding: '0.6rem', fontWeight: 600, color: 'hsl(var(--foreground))', textAlign: 'left' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', justifyContent: 'flex-start', textAlign: 'left' }}>
                          {dish.name}
                        </div>
                      </td>
                      <td style={{ padding: '0.6rem', textAlign: 'right', color: 'hsl(var(--muted-foreground))' }}>
                        {formatAuditPortionQuantity(Number(dish.portionGrams || dish.mappedGrams || 0), unitSymbol)}
                      </td>
                      <td style={{ padding: '0.6rem', textAlign: 'right', fontWeight: 600, color: 'hsl(var(--primary))' }}>
                        {Number(dish.platesCanBeMade || 0).toLocaleString()}
                      </td>
                      <td style={{ padding: '0.6rem', textAlign: 'right', color: 'hsl(var(--success))' }}>
                        {Number(dish.platesSold || 0).toLocaleString()}
                      </td>
                      <td style={{ padding: '0.6rem', textAlign: 'right', fontWeight: 700, color: leakColor }}>
                        {dishOverLeak > 0 ? `+${dishOverLeak}` : (dishUnderLeak > 0 ? `-${dishUnderLeak}` : '0')}
                      </td>
                      <td style={{ padding: '0.6rem', textAlign: 'right', color: leakColor, fontSize: '0.78rem' }}>
                        {Number(dish.leakGrams || 0) > 0 ? formatAuditAnchorQuantity(Number(dish.leakGrams), unitSymbol) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {dishes.length > 1 && (
                <tfoot>
                  <tr style={{ borderTop: '2px solid hsl(var(--border))' }}>
                    <td style={{ padding: '0.6rem', fontWeight: 700, textAlign: 'left' }}>Total</td>
                    <td style={{ padding: '0.6rem', textAlign: 'right' }}>—</td>
                    <td style={{ padding: '0.6rem', textAlign: 'right', fontWeight: 700, color: 'hsl(var(--primary))' }}>{platesCanBeMade}</td>
                    <td style={{ padding: '0.6rem', textAlign: 'right', fontWeight: 700, color: 'hsl(var(--success))' }}>{totalPlatesSold}</td>
                    <td style={{ padding: '0.6rem', textAlign: 'right', fontWeight: 700, color: leakPlates > 0 ? 'hsl(var(--danger))' : 'hsl(var(--muted-foreground))' }}>
                      {leakPlates > 0 ? (
                        totalOverLeakPlates > 0 && totalUnderLeakPlates > 0 
                          ? `+${totalOverLeakPlates} / -${totalUnderLeakPlates}` 
                          : totalOverLeakPlates > 0 
                            ? `+${totalOverLeakPlates}` 
                            : `-${totalUnderLeakPlates}`
                      ) : '0'}
                    </td>
                    <td style={{ padding: '0.6rem', textAlign: 'right', fontWeight: 700, color: totalLeakGrams > 0 ? 'hsl(var(--danger))' : 'hsl(var(--muted-foreground))' }}>
                      {totalLeakGrams > 0 ? formatAuditAnchorQuantity(totalLeakGrams, unitSymbol) : '—'}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        ) : (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            minHeight: '80px', border: '1px dashed hsl(var(--border))', borderRadius: '8px',
            color: 'hsl(var(--muted-foreground))', fontSize: '0.85rem',
          }}>
            No dish mappings found for this period
          </div>
        )}

        {/* Verdict */}
        {hasLeakage && (
          <div style={{
            marginTop: '1rem',
            padding: '0.8rem 1rem',
            borderRadius: '8px',
            background: '#ffffff',
            border: '1px solid #cdcdcd',
            fontSize: '0.82rem',
            color: 'hsl(var(--foreground))',
          }}>
            <strong style={{ color: 'hsl(var(--danger))', display: 'block', marginBottom: '0.5rem', fontSize: '0.85rem' }}>
              Leakage Verdict:
            </strong>
            <ul style={{ listStyleType: 'disc', paddingLeft: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.35rem', textAlign: 'left' }}>
              <li>
                {leakPlates} plate mismatch for <strong>{ingredient}</strong>
              </li>
              <li>
                Breakup: over-use <strong>{totalOverLeakPlates}</strong>, under-pour <strong>{totalUnderLeakPlates}</strong>
              </li>
              <li>
                {mismatchLabel}: <strong>{formatAuditAnchorQuantity(totalLeakGrams, unitSymbol)}</strong>
              </li>
              {dishes.filter(d => Number(d.leakPlates) > 0).length > 0 && (
                <li>
                  Highest leakage: <strong style={{ color: 'hsl(var(--danger))' }}>
                    {dishes.filter(d => Number(d.leakPlates) > 0).sort((a, b) => b.leakPlates - a.leakPlates)[0]?.name}
                  </strong> ({dishes.filter(d => Number(d.leakPlates) > 0).sort((a, b) => b.leakPlates - a.leakPlates)[0]?.leakPlates} plates)
                </li>
              )}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

const BatchConsumptionHub = ({
  ingredient,
  masterBatchGrams,
  activeBatchGrams,
  reservedStockGrams,
  accountedGrams,
  salesGrams,
  actualUsageGrams,
  varianceBeforeWasteGrams,
  wasteGrams,
  leakGrams,
  leakKg,
  carryForwardGrams,
  carryForwardKg,
  leakValuePerKg,
  wasteKg,
  sales,
  unitSymbol,
  serverEfficiency,
}) => {
  const unitFamily = getAuditUnitFamily(unitSymbol);
  const leakStatLabel = unitFamily === 'mass'
    ? 'Leak Weight'
    : (unitFamily === 'volume' ? 'Leak Volume' : 'Leak Quantity');
  const valuationRateLabel = getAuditRateLabel(unitSymbol);
  const formatQuantity = (anchorValue, maxDigits = 4) => formatAuditAnchorQuantity(anchorValue, unitSymbol, maxDigits);
  const formatPortion = (anchorValue, maxDigits = 3) => formatAuditPortionQuantity(anchorValue, unitSymbol, maxDigits);
  const formatSubQuantity = (anchorValue, maxDigits = 3) => formatAuditAnchorSub(anchorValue, unitSymbol, maxDigits);

  const [activePhase, setActivePhase] = useState('consumption');
  const [hoveredSegment, setHoveredSegment] = useState(null);
  const [hoveredSale, setHoveredSale] = useState(null);
  const [showProof, setShowProof] = useState(true);

  // Corrected leakage logic:
  // - Window starts from first TSR date and ends at selected closing date.
  // - Consumed stock = opening at start + stock-ins in window - closing stock.
  // - Active batch = mapped expected usage from TSR sales.
  // - Final leak = max(0, consumed stock - mapped expected usage).
  const safeMasterBatchGrams = Math.max(0, Number(masterBatchGrams || 0));
  const safeSalesGrams = Math.max(0, Number(salesGrams || 0));
  const safeWasteGrams = Math.max(0, Number(wasteGrams || 0));
  const safeLeakGrams = Math.max(0, Number(leakGrams || 0));
  const flowTotalGrams = safeSalesGrams + safeWasteGrams + safeLeakGrams;

  const wastePercent = safeMasterBatchGrams > 0 ? (safeWasteGrams / safeMasterBatchGrams) * 100 : 0;
  const leakPercent = safeMasterBatchGrams > 0 ? (safeLeakGrams / safeMasterBatchGrams) * 100 : 0;

  // Use normalized percentages for visual bars so segments always add up to 100%.
  const salesStackPercent = flowTotalGrams > 0 ? (safeSalesGrams / flowTotalGrams) * 100 : 0;
  const wasteStackPercent = flowTotalGrams > 0 ? (safeWasteGrams / flowTotalGrams) * 100 : 0;
  const leakStackPercent = flowTotalGrams > 0 ? (safeLeakGrams / flowTotalGrams) * 100 : 0;

  // Single source of truth: the server computes efficiency from the leak identity
  // (auditCore). Use it verbatim; only fall back to a client estimate if the
  // server did not supply one (legacy payloads).
  const serverEfficiencyNum = Number(serverEfficiency);
  const hasServerEfficiency = Number.isFinite(serverEfficiencyNum);
  const efficiencyRaw = hasServerEfficiency
    ? serverEfficiencyNum
    : (safeMasterBatchGrams > 0 ? (((safeMasterBatchGrams - safeLeakGrams) / safeMasterBatchGrams) * 100) : 0);
  const efficiencyVisual = Math.max(0, Math.min(100, efficiencyRaw));
  const efficiency = Math.round(efficiencyVisual);
  const clampPercent = (value) => Math.max(0, Math.min(100, Number(value || 0)));
  const hasPercentOverflow = safeMasterBatchGrams > 0 && flowTotalGrams > safeMasterBatchGrams;
  const accountedPercent = safeMasterBatchGrams > 0 ? ((accountedGrams / safeMasterBatchGrams) * 100).toFixed(0) : 0;
  const leakFinancialValue = (Number(leakGrams || 0) / 1000) * Number(leakValuePerKg || 0);

  console.log('📊 [FUEL TANK] Calculation Check:', {
    bought: `${masterBatchGrams}g (100%)`,
    activeBatch: `${activeBatchGrams}g`,
    reservedStock: `${reservedStockGrams}g`,
    actualUsage: `${actualUsageGrams}g`,
    varianceBeforeWaste: `${varianceBeforeWasteGrams}g`,
    waste: `${wasteGrams}g (${wastePercent.toFixed(1)}%)`,
    leak: `${leakGrams}g (${leakPercent.toFixed(1)}%)`,
    carryForward: `${carryForwardGrams}g`,
    efficiency: `${efficiency}% non-leak`
  });

  const segments = [
    {
      key: 'sales',
      label: 'Sales',
      grams: salesGrams,
      percent: salesStackPercent,
      segClass: 'seg-sales',
      dotClass: 'dot-sales',
      colorClass: 'color-success',
      Icon: Droplets,
      description: 'Expected ingredient usage from recipe mapping (plates sold × mapped grams)',
    },
    {
      key: 'waste',
      label: 'Waste',
      grams: wasteGrams,
      percent: wasteStackPercent,
      segClass: 'seg-waste',
      dotClass: 'dot-waste',
      colorClass: 'color-warning',
      Icon: Trash2,
      description: 'Recorded wastage logs',
    },
    {
      key: 'leak',
      label: 'Leak',
      grams: leakGrams,
      percent: leakStackPercent,
      segClass: 'seg-leak',
      dotClass: 'dot-leak',
      colorClass: 'color-danger',
      Icon: AlertTriangle,
      description: 'Unaccounted loss (missing from inventory)',
    },
  ].filter(seg => seg.percent > 0); // Only show segments with actual values

  const phases = [
    { key: 'consumption', label: 'Fuel Tank', Icon: Fuel },
    { key: 'sync', label: 'Flow Sync', Icon: FileCheck },
    { key: 'variance', label: 'Variance', Icon: Search },
  ];

  return (
    <div className="glass-card bch-wrapper">
      <div className={`bch-bg-blob phase-${activePhase}`} />

      <h3 className="section-heading">Consumptions, TSR & Variance</h3>

      <div className="bch-tabs">
        {phases.map((phase, i) => {
          const isActive = activePhase === phase.key;
          return (
            <button
              key={phase.key}
              className={`bch-tab ${isActive ? 'active' : ''}`}
              onClick={() => setActivePhase(phase.key)}
            >
              <phase.Icon style={{ width: 16, height: 16 }} />
              <span className="bch-tab-icon-label">{phase.label}</span>
              {isActive && <div className="bch-tab-indicator" />}
            </button>
          );
        })}
      </div>

      <div className="bch-content">
        {activePhase === 'consumption' && (
          <div className="bch-phase" key="consumption">
            <div className="fuel-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div>
                  <h3 className="fuel-title">{ingredient}</h3>
                  <p className="fuel-sub">{formatQuantity(masterBatchGrams)} Master Batch Breakdown</p>
                </div>
              </div>
              <div style={{
                fontSize: '0.9rem',
                fontWeight: '700',
                color: 'hsl(var(--success))',
                whiteSpace: 'nowrap',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0.35rem 0.75rem',
                background: 'hsl(var(--success) / 0.1)',
                border: '1px solid hsl(var(--success) / 0.2)',
                borderRadius: '8px'
              }}>
                Sales: {formatQuantity(salesGrams)}
              </div>
            </div>

            <div className="stacked-bar">
              {segments.map((seg) => (
                <div
                  key={seg.key}
                  className={`stacked-segment ${seg.segClass}`}
                  style={{ width: `${clampPercent(seg.percent)}%` }}
                >
                  <span className="stacked-segment-label">{clampPercent(seg.percent) >= 8 ? `${clampPercent(seg.percent).toFixed(0)}%` : ''}</span>
                </div>
              ))}
            </div>



            <div className="stacked-legend">
              {segments.filter(seg => seg.key !== 'sales').map((seg) => (
                <div
                  key={seg.key}
                  className="legend-item"
                >
                  <div className={`legend-dot ${seg.dotClass}`} />
                  <span className="color-muted">
                    {seg.label} <span className={`legend-value ${seg.colorClass}`}>{formatQuantity(seg.grams)}</span>
                  </span>
                </div>
              ))}
            </div>

            <button className="phase-cta cta-sync" onClick={() => setActivePhase('sync')}>
              <span>View Flow Breakdown</span>
              <ArrowRight style={{ width: 16, height: 16 }} />
            </button>
          </div>
        )}

        {activePhase === 'sync' && (
          <div className="bch-phase" key="sync">
            <div className="tsr-header">
              <div>
                <h3 className="fuel-title">Flow Synchronization</h3>
              </div>
            </div>

            <div className="tsr-entries">
              {sales.map((entry, i) => {
                const entryPercent = (entry.totalGrams / masterBatchGrams) * 100;
                return (
                  <AnimatedTsrEntry key={entry.name} index={i}>
                    <div
                      className={`tsr-entry ${hoveredSale === i ? 'hovered' : ''}`}
                      onMouseEnter={() => setHoveredSale(i)}
                      onMouseLeave={() => setHoveredSale(null)}
                    >
                      <div className="tsr-entry-fill" style={{ width: `${clampPercent(entryPercent)}%` }} />
                      <div className="tsr-entry-content">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <div>
                            <p className="tsr-entry-name">{entry.name}</p>
                            <p className="tsr-entry-detail">
                              {entry.platesSold} plates × {formatPortion(entry.portionGrams)}
                            </p>
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <p className="tsr-entry-kg" style={{ color: '#000000' }}>
                            {formatQuantity(entry.totalGrams)} / {formatQuantity(masterBatchGrams)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </AnimatedTsrEntry>
                );
              })}
            </div>

            <AnimatedTsrEntry index={sales.length}>
              <div className="tsr-waste">
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <div>
                    <p className="tsr-entry-name">Recorded Wastage</p>
                    <p className="tsr-entry-detail">Logged waste entries</p>
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <p className="tsr-waste-kg">
                    {formatQuantity(wasteGrams)} / {formatQuantity(masterBatchGrams)}
                  </p>
                </div>
              </div>
            </AnimatedTsrEntry>

            <AnimatedTsrEntry index={sales.length + 1}>
              <div className="tsr-total">
                <div className="tsr-total-header" style={{ marginBottom: 0 }}>
                  <div className="tsr-total-label">
                    <span>Total Accounted</span>
                  </div>
                  <span className="tsr-total-value">
                    {formatQuantity(accountedGrams)} / {formatQuantity(masterBatchGrams)}
                  </span>
                </div>
              </div>
            </AnimatedTsrEntry>

            <AnimatedTsrEntry index={sales.length + 2}>
              <button 
                className="phase-cta" 
                onClick={() => setActivePhase('variance')}
                style={{
                  marginTop: '1.25rem',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  padding: '0.625rem',
                  borderRadius: '0.75rem',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  transition: 'transform 0.2s, background 0.2s',
                  background: 'transparent',
                  border: '1px solid #e0e0e0',
                  color: '#000000'
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'hsl(var(--muted) / 0.1)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <span>Carry-forward: {formatQuantity(carryForwardGrams)} | Leak variance: {formatQuantity(leakGrams)}</span>
                <ArrowRight style={{ width: 16, height: 16 }} />
              </button>
            </AnimatedTsrEntry>
          </div>
        )}

        {activePhase === 'variance' && (
          <div className="bch-phase" key="variance">
            <div className="var-header">
              <div style={{ flex: 1 }}>
                <h3 className="fuel-title">Variance Detected</h3>
              </div>
              <button className="var-proof-toggle" onClick={() => setShowProof(!showProof)}>
                {showProof ? 'Hide Proof' : 'Show Proof'}
              </button>
            </div>

            <div className="var-equation">
              <div className="var-eq-item">
                <p className="var-eq-label">Actual Used</p>
                <p className="var-eq-value color-foreground">{formatQuantity(actualUsageGrams)}</p>
              </div>
              <span className="var-eq-operator">−</span>
              <div className="var-eq-item">
                <p className="var-eq-label">Active Batch</p>
                <p className="var-eq-value color-success">{formatQuantity(salesGrams)}</p>
              </div>
              <span className="var-eq-operator">=</span>
              <div className="var-eq-item">
                <p className="var-eq-label">Variance</p>
                <p className="var-eq-value color-warning">{formatQuantity(varianceBeforeWasteGrams)}</p>
              </div>
            </div>

            <div className="var-equation" style={{ marginTop: '0.75rem' }}>
              <div className="var-eq-item">
                <p className="var-eq-label">Variance</p>
                <p className="var-eq-value color-warning">{formatQuantity(varianceBeforeWasteGrams)}</p>
              </div>
              <span className="var-eq-operator">−</span>
              <div className="var-eq-item">
                <p className="var-eq-label">Wastage</p>
                <p className="var-eq-value color-success">{formatQuantity(wasteGrams)}</p>
              </div>
              <span className="var-eq-operator">=</span>
              <div className="var-eq-item">
                <p className="var-eq-label">Final Leak</p>
                <p className="var-eq-value color-danger">{formatQuantity(leakGrams)}</p>
              </div>
            </div>

            <div className={`var-proof ${showProof ? 'open' : ''}`}>
              <div className="var-proof-inner">
                <div className="var-proof-title">
                  <span>Mathematical Proof</span>
                </div>
                <p className="var-proof-line">
                  Master Batch: {formatQuantity(masterBatchGrams)}
                  {formatSubQuantity(masterBatchGrams) ? ` (${formatSubQuantity(masterBatchGrams)})` : ''}
                </p>
                <p className="var-proof-line">
                  Carry Forward: {formatQuantity(carryForwardGrams)}
                </p>
                <p className="var-proof-line danger">
                  Final Leak: ({formatQuantity(actualUsageGrams)} − {formatQuantity(salesGrams)}) − {formatQuantity(wasteGrams)} = {formatQuantity(leakGrams)}
                </p>
                <p className="var-proof-line">
                  FIFO Valuation: {formatQuantity(leakGrams)} × ₹{leakValuePerKg}/{valuationRateLabel} = ₹{leakFinancialValue.toLocaleString()}
                </p>
              </div>
            </div>

            <div className="var-stats">
              {[
                { label: leakStatLabel, value: formatQuantity(leakGrams), sub: formatSubQuantity(leakGrams) },
                { label: 'Leak %', value: `${leakPercent.toFixed(0)}%`, sub: 'of master batch' },
                { label: 'Financial Loss', value: `₹${leakFinancialValue.toLocaleString()}`, sub: `@ ₹${leakValuePerKg}/${valuationRateLabel} (FIFO)` },
              ].map((stat) => (
                <div key={stat.label} className="var-stat">
                  <p className="var-stat-label">{stat.label}</p>
                  <p className="var-stat-value">{stat.value}</p>
                  <p className="var-stat-sub">{stat.sub}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ReverseTrackingFlow = ({
  boughtAnchor,
  wasteAnchor,
  expectedUsageAnchor,
  actualUsageAnchor,
  leakAnchor,
  carryForwardAnchor,
  soldPlates,
  pricePerKg,
  unitSymbol,
}) => {
  const hasLeakage = Number(leakAnchor || 0) > 0;
  const valuationRateLabel = getAuditRateLabel(unitSymbol);
  const leakFinancialValue = (Number(leakAnchor || 0) / 1000) * Number(pricePerKg || 0);
  const formatQuantity = (anchorValue, maxDigits = 4) => formatAuditAnchorQuantity(anchorValue, unitSymbol, maxDigits);
  const formatSubQuantity = (anchorValue, maxDigits = 3) => formatAuditAnchorSub(anchorValue, unitSymbol, maxDigits);

  const steps = [
    {
      label: 'Bought',
      value: formatQuantity(boughtAnchor),
      sublabel: formatSubQuantity(boughtAnchor),
      Icon: Package,
      colorClass: 'color-foreground',
      stepClass: 'step-default',
      detail: 'Total purchased weight for the selected TSR-to-closing period',
    },
    {
      label: 'Wastage',
      value: formatQuantity(wasteAnchor),
      sublabel: formatSubQuantity(wasteAnchor),
      Icon: Trash2,
      colorClass: 'color-warning',
      stepClass: 'step-warning',
      detail: 'Wastage recorded by kitchen staff in daily logs',
    },
    {
      label: 'Expected',
      value: formatQuantity(expectedUsageAnchor),
      sublabel: formatSubQuantity(expectedUsageAnchor),
      Icon: ChefHat,
      colorClass: 'color-success',
      stepClass: 'step-success',
      detail: `Based on sales mapping: plates sold × recipe portion = ${formatQuantity(expectedUsageAnchor)} expected usage`,
    },
    {
      label: 'Leak',
      value: formatQuantity(leakAnchor),
      sublabel: '',
      Icon: HelpCircle,
      colorClass: 'color-danger',
      stepClass: 'step-danger',
      detail: `Usage variance: ${formatQuantity(actualUsageAnchor)} actual usage - ${formatQuantity(expectedUsageAnchor)} expected usage = ${formatQuantity(leakAnchor)} leak (valued at ₹${pricePerKg}/${valuationRateLabel})`,
    },
  ];

  return (
    <div className="glass-card rt-card" style={{ padding: '1rem' }}>
      <div className="rt-bg-blob" />
      <div className="rt-inner">
        <h3 className="rt-title">Reverse Tracking & Portion Drift</h3>

        <div className="rt-flow" style={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
          {steps.map((step, i) => (
            <React.Fragment key={step.label}>
              <div
                className={`rt-step ${step.stepClass}`}
                style={{ 
                  animationDelay: `${i * 0.15}s`,
                  flex: 1,
                  margin: '0 0.35rem'
                }}
              >
                <span className={`rt-step-value ${step.colorClass}`}>{step.value}</span>
                <span className="rt-step-label">{step.label}</span>
                <span className="rt-step-sub">{step.sublabel}</span>
              </div>
              {i < steps.length - 1 && <ArrowRight className="rt-arrow" style={{ width: 16, height: 16, flexShrink: 0, margin: '0 0.15rem' }} />}
            </React.Fragment>
          ))}
        </div>

        <div className="rt-summary">
          <ul className="rt-summary-list">
            <li className="rt-summary-item">
              <span className="highlight-danger">{formatQuantity(leakAnchor)}</span>{formatSubQuantity(leakAnchor) ? ` (${formatSubQuantity(leakAnchor)})` : ''} is usage variance (leakage).
            </li>
            <li className="rt-summary-item">
              <span className="highlight-foreground">{formatQuantity(carryForwardAnchor)}</span> is carry-forward stock for the next closing cycle.
            </li>
            <li className="rt-summary-item">
              {hasLeakage
                ? `This extra usage across ${soldPlates} sold plates suggests over-portioning.`
                : `No excess usage detected across ${soldPlates} sold plates for this cycle.`}
            </li>
            <li className="rt-summary-item">
              FIFO Consumption: Leak valued at most recent price <span className="mono">₹{pricePerKg}/{valuationRateLabel}</span> ={' '}
              <span className="mono highlight-danger">₹{leakFinancialValue.toLocaleString()}</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

const PortionDriftDetailed = ({ leakGrams, dishes, ingredientItemId, unitSymbol, onApplyFixPortion }) => {
  const normalizeUnit = (rawUnit) => {
    const u = String(rawUnit || '').trim().toLowerCase();
    if (['g', 'gm', 'gram', 'grams'].includes(u)) return 'g';
    if (['kg', 'kilogram', 'kilograms'].includes(u)) return 'kg';
    if (['mg', 'milligram', 'milligrams'].includes(u)) return 'mg';
    if (['ml', 'milliliter', 'milliliters', 'millilitre', 'millilitres'].includes(u)) return 'ml';
    if (['l', 'lt', 'liter', 'liters', 'litre', 'litres'].includes(u)) return 'l';
    return String(rawUnit || 'g');
  };

  const getUnitFamily = (u) => {
    if (['g', 'kg', 'mg'].includes(u)) return 'mass';
    if (['ml', 'l'].includes(u)) return 'volume';
    return 'other';
  };

  const getUnitOptions = (baseUnit) => {
    const family = getUnitFamily(baseUnit);
    if (family === 'mass') return ['mg', 'g', 'kg'];
    if (family === 'volume') return ['ml', 'l'];
    return [baseUnit];
  };

  const toAnchor = (value, unit) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return 0;
    if (unit === 'mg') return n / 1000;
    if (unit === 'kg') return n * 1000;
    if (unit === 'l') return n * 1000;
    return n;
  };

  const fromAnchor = (value, unit) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return 0;
    if (unit === 'mg') return n * 1000;
    if (unit === 'kg') return n / 1000;
    if (unit === 'l') return n / 1000;
    return n;
  };

  const formatConvertedQuantity = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return '0';
    if (n === 0) return '0';
    const fixed = n.toFixed(9).replace(/\.?0+$/, '');
    return fixed === '0' ? n.toPrecision(3) : fixed;
  };

  const formatAutoQuantity = (value, unit) => {
    const n = Number(value);
    const normalized = normalizeUnit(unit || 'g');
    if (!Number.isFinite(n)) return { value: '0', unit: normalized };
    if (normalized === 'g' && Math.abs(n) >= 1000) {
      return { value: formatConvertedQuantity(n / 1000), unit: 'kg' };
    }
    return { value: formatConvertedQuantity(n), unit: normalized };
  };

  const hasMeasurableDrift = (dish) => Math.abs(Number(dish?.extraGrams || 0)) > 0.000001;
  const getDriftRatio = (dish) => {
    const mapped = Math.max(1, Number(dish?.mappedGrams || 0));
    return Math.abs(Number(dish?.extraGrams || 0)) / mapped;
  };

  const baseUnit = normalizeUnit(unitSymbol || 'g');
  const MIN_MANUAL_QUANTITY = 0.001;
  const unitOptions = getUnitOptions(baseUnit);
  const unitLabel = baseUnit;
  const worstIndex = dishes.reduce(
    (max, d, i) => getDriftRatio(d) > getDriftRatio(dishes[max]) ? i : max,
    0,
  );
  const [selectedDish, setSelectedDish] = useState(null);
  const [isApplyingFix, setIsApplyingFix] = useState(false);
  const [popupStep, setPopupStep] = useState(null);
  const [pendingDish, setPendingDish] = useState(null);
  const [quantityInput, setQuantityInput] = useState('');
  const [selectedUnit, setSelectedUnit] = useState(baseUnit);
  const [calculatedBaseQuantity, setCalculatedBaseQuantity] = useState(0);
  const [isManualQuantityRequired, setIsManualQuantityRequired] = useState(false);
  const [resultPopup, setResultPopup] = useState({ title: '', message: '' });
  const shouldForceKg = baseUnit === 'g' && Number(calculatedBaseQuantity) >= 1000;
  const effectiveUnitOptions = shouldForceKg ? ['kg'] : unitOptions;

  const computeCalculatedQuantityForDish = (dish) => {
    const mappedQty = Number(dish?.mappedGrams);
    const extraQty = Number(dish?.extraGrams);
    const actualFromCalculation = Number(dish?.actualGrams);
    const calculatedQty = Number.isFinite(actualFromCalculation) && actualFromCalculation > 0
      ? actualFromCalculation
      : (Number.isFinite(mappedQty) ? mappedQty : 0) + (Number.isFinite(extraQty) ? extraQty : 0);
    return Number.isFinite(calculatedQty) ? calculatedQty : 0;
  };

  const getSuggestedQuantityForDish = (dish) => {
    // Bug 10: default the suggested value to the RECIPE portion (mappedGrams),
    // NOT the drifted actualGrams. Baking the drift into the mapping just makes
    // the wrong number permanent; the fix should pull the recipe back to its
    // intended portion. Drift is shown as info; the user confirms explicitly.
    const mappedQty = Number(dish?.mappedGrams);
    if (Number.isFinite(mappedQty) && mappedQty > 0) {
      return {
        quantity: mappedQty,
        requiresManual: false,
      };
    }

    const inferredQty = computeCalculatedQuantityForDish(dish);
    if (Number.isFinite(inferredQty) && inferredQty > 0) {
      return {
        quantity: inferredQty,
        requiresManual: true,
      };
    }

    return {
      quantity: MIN_MANUAL_QUANTITY,
      requiresManual: true,
    };
  };

  const openResultPopup = (title, message) => {
    setResultPopup({ title, message });
    setPopupStep('result');
  };

  const closePopup = () => {
    setPopupStep(null);
    setPendingDish(null);
    setQuantityInput('');
    setSelectedUnit(baseUnit);
    setCalculatedBaseQuantity(0);
    setIsManualQuantityRequired(false);
  };

  const handleFixPortionSizes = async () => {
    if (!Array.isArray(dishes) || dishes.length === 0) {
      openResultPopup('Cannot Apply Fix', 'No dish-level data available to update portion mapping.');
      return;
    }

    const driftDishes = dishes.filter((d) => hasMeasurableDrift(d));
    if (driftDishes.length === 0) {
      openResultPopup('No Portion Drift', 'Current dishes show zero drift. No mapping correction is required.');
      return;
    }

    if (selectedDish === null) {
      openResultPopup('Select Dish First', 'Tap the exact dish card you want to fix, then click Fix Portion Sizes.');
      return;
    }

    const targetDish = dishes[selectedDish];
    if (!targetDish || !targetDish.menuItemId || !ingredientItemId) {
      openResultPopup('Cannot Apply Fix', 'Missing dish/ingredient mapping details. Please refresh and try again.');
      return;
    }

    if (!hasMeasurableDrift(targetDish)) {
      openResultPopup('No Drift', `${targetDish.name} currently has no measurable drift to fix.`);
      return;
    }

    const { quantity: rawSuggestedQty, requiresManual } = getSuggestedQuantityForDish(targetDish);
    const suggestedQty = Number(rawSuggestedQty.toFixed(6));

    setPendingDish(targetDish);
    setIsManualQuantityRequired(requiresManual);
    setCalculatedBaseQuantity(suggestedQty);
    const defaultUnit = baseUnit === 'g' && suggestedQty >= 1000 ? 'kg' : baseUnit;
    setSelectedUnit(defaultUnit);
    setQuantityInput(formatConvertedQuantity(fromAnchor(toAnchor(suggestedQty, baseUnit), defaultUnit)));
    setPopupStep('input');
  };

  const handleTargetDishChange = (menuItemIdValue) => {
    const nextDish = dishes.find((d) => Number(d.menuItemId) === Number(menuItemIdValue));
    if (!nextDish) return;

    const { quantity: rawNextQty, requiresManual } = getSuggestedQuantityForDish(nextDish);
    const nextQty = Number(rawNextQty.toFixed(6));

    const nextIndex = dishes.findIndex((d) => Number(d.menuItemId) === Number(menuItemIdValue));
    if (nextIndex >= 0) setSelectedDish(nextIndex);

    setPendingDish(nextDish);
    setIsManualQuantityRequired(requiresManual);
    setCalculatedBaseQuantity(Number(nextQty.toFixed(6)));
    const defaultUnit = baseUnit === 'g' && nextQty >= 1000 ? 'kg' : baseUnit;
    setSelectedUnit(defaultUnit);
    setQuantityInput(formatConvertedQuantity(fromAnchor(toAnchor(nextQty, baseUnit), defaultUnit)));
  };

  const handleUnitChange = (newUnit) => {
    if (shouldForceKg) return;
    const anchorValue = toAnchor(calculatedBaseQuantity, baseUnit);
    const converted = fromAnchor(anchorValue, newUnit);
    setSelectedUnit(newUnit);
    setQuantityInput(formatConvertedQuantity(converted));
  };

  const handleInputNext = () => {
    const parsed = parseFloat(quantityInput);
    const parsedBase = fromAnchor(toAnchor(parsed, selectedUnit), baseUnit);
    if (!Number.isFinite(parsed) || parsed <= 0 || !Number.isFinite(parsedBase) || parsedBase <= 0) {
      openResultPopup('Invalid Quantity', `Please enter a valid positive quantity in ${selectedUnit}.`);
      return;
    }
    setCalculatedBaseQuantity(parsedBase);
    setPopupStep('confirm-1');
  };

  const handleFinalApply = async () => {
    if (!pendingDish) return;

    const newQuantity = Number(calculatedBaseQuantity);
    if (!Number.isFinite(newQuantity) || newQuantity <= 0) {
      openResultPopup('Invalid Quantity', `Please enter a valid positive quantity in ${unitLabel}.`);
      return;
    }

    try {
      setIsApplyingFix(true);
      const result = await onApplyFixPortion({ targetDish: pendingDish, newQuantity, unitSymbol: baseUnit });
      if (result?.ok) {
        openResultPopup('Portion Mapping Updated', result.message || 'Portion mapping updated successfully.');
      } else {
        openResultPopup('Update Failed', result?.message || 'Failed to update portion mapping.');
      }
    } finally {
      setIsApplyingFix(false);
    }
  };

  return (
    <div
      className="glass-card pd-card"
      style={{ padding: '1rem', width: '100%', maxWidth: '600px', minHeight: '318px', display: 'flex', flexDirection: 'column' }}
    >
      <div className="pd-bg-blob" />
      <div className="pd-inner" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div className="pd-header">
          <div>
            <h3 className="pd-title">Portion Drift Analysis</h3>
          </div>
        </div>

        {dishes.length === 0 ? (
          <div style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid hsl(var(--border))',
            borderRadius: '8px',
            marginBottom: '1rem',
            color: 'hsl(var(--muted-foreground))',
            fontSize: '0.9rem',
            minHeight: '100px'
          }}>
            No batches recorded
          </div>
        ) : (
          <div className="pd-dishes">
            {dishes.map((dish, i) => {
              const safeMapped = Math.max(1, Number(dish.mappedGrams || 0));
              const signedExtra = Number(dish.extraGrams || 0);
              const rawDriftPercent = (signedExtra / safeMapped) * 100;
              const driftPercent = Math.min(999, Math.abs(rawDriftPercent)).toFixed(0);
              const driftLabel = rawDriftPercent > 0 ? 'over portion drift' : rawDriftPercent < 0 ? 'under portion drift' : 'drift';
              const isWorst = i === worstIndex;
              const isSelected = selectedDish === i;
              const severity = signedExtra / safeMapped;
              const absoluteSeverity = Math.abs(severity);
              const sevClass = absoluteSeverity > 0.2 ? 'sev-high' : absoluteSeverity > 0.1 ? 'sev-mid' : 'sev-low';
              const colorClass = absoluteSeverity > 0.2 ? 'color-danger' : absoluteSeverity > 0.1 ? 'color-warning' : 'color-success';
              const fillClass = absoluteSeverity > 0.2 ? 'fill-danger' : absoluteSeverity > 0.1 ? 'fill-warning' : 'fill-success';
              const barWidth = Math.min((Number(dish.actualGrams || 0) / safeMapped) * 50, 100);
              const mappedDisplay = formatAutoQuantity(dish.mappedGrams, unitLabel);
              const actualDisplay = formatAutoQuantity(dish.actualGrams, unitLabel);
              const extraDisplay = formatAutoQuantity(Math.abs(signedExtra), unitLabel);
              const extraSign = signedExtra > 0 ? '+' : signedExtra < 0 ? '-' : '';
              const leakDisplay = formatAutoQuantity(leakGrams, unitLabel);
              const totalExtraDisplay = formatAutoQuantity(Math.abs(signedExtra) * dish.platesSold, unitLabel);
              const totalVarianceLabel = signedExtra > 0 ? 'wasted' : signedExtra < 0 ? 'short-served' : 'balanced';

            return (
              <div
                key={dish.name}
                className={`pd-dish ${sevClass} ${isSelected ? 'selected' : ''} ${isWorst && !isSelected ? 'worst' : ''}`}
                onClick={() => setSelectedDish(isSelected ? null : i)}
                style={{ animationDelay: `${i * 0.15}s` }}
              >
                <div className="pd-dish-content">
                  <div className="pd-dish-top">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <p className="pd-dish-name">{dish.name}</p>
                      {isWorst && <span className="pd-worst-badge">Worst</span>}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span
                        className={`pd-drift-badge ${colorClass}`}
                        style={{ background: '#fff', color: '#000' }}
                      >
                        {driftPercent}% {driftLabel}
                      </span>
                      <Eye style={{ width: 14, height: 14, color: 'hsl(var(--muted-foreground))' }} />
                    </div>
                  </div>

                  <div className="pd-scale-grid">
                    <div className="pd-scale-item map">
                      <p className="pd-scale-label">Map</p>
                      <p className="pd-scale-value color-success">{mappedDisplay.value}{mappedDisplay.unit}</p>
                    </div>
                    <div className="pd-scale-item actual">
                      <p className="pd-scale-label">Actual</p>
                      <p className="pd-scale-value color-danger">{actualDisplay.value}{actualDisplay.unit}</p>
                    </div>
                    <div className={`pd-scale-item ${absoluteSeverity > 0.2 ? 'extra-high' : 'extra-mid'}`}>
                      <p className="pd-scale-label">Extra</p>
                      <p className={`pd-scale-value ${colorClass}`}>{extraSign}{extraDisplay.value}{extraDisplay.unit}</p>
                    </div>
                  </div>

                  <div className="pd-drift-bar">
                    <div className="pd-drift-bar-mark" />
                    <div className={`pd-drift-bar-fill ${fillClass}`} style={{ width: `${barWidth}%` }} />
                  </div>
                  <div className="pd-drift-footer">
                    <span>0{unitLabel}</span>
                    <span className="mono color-success">{mappedDisplay.value}{mappedDisplay.unit} ideal</span>
                    <span className={`mono ${colorClass}`}>{actualDisplay.value}{actualDisplay.unit} actual</span>
                  </div>
                </div>

                <div className={`pd-dish-expanded ${isSelected ? 'open' : ''}`}>
                  <div className="pd-dish-expanded-inner">
                    <div style={{ paddingTop: '0.75rem', borderTop: '1px solid hsl(var(--border) / 0.3)' }}>
                      <div className="pd-detail-row">
                        <ArrowRight style={{ width: 12, height: 12 }} />
                        <span>
                          Calculation: {leakDisplay.value}{leakDisplay.unit} ÷ {dish.platesSold} plates = <span className={`mono ${colorClass}`}>{extraSign}{extraDisplay.value}{extraDisplay.unit}</span> per
                          plate
                        </span>
                      </div>
                      <div className="pd-detail-row">
                        <ArrowRight style={{ width: 12, height: 12 }} />
                        <span>
                          Chef is serving <span className="mono color-danger">{actualDisplay.value}{actualDisplay.unit}</span> instead of <span className="mono color-success">{mappedDisplay.value}{mappedDisplay.unit}</span>
                        </span>
                      </div>
                      <div className="pd-detail-row">
                        <ArrowRight style={{ width: 12, height: 12 }} />
                        <span>
                          Total variance: {extraSign}{extraDisplay.value}{extraDisplay.unit} × {dish.platesSold} plates = <span className="mono color-foreground">{totalExtraDisplay.value}{totalExtraDisplay.unit}</span>{' '}
                          {totalVarianceLabel}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          </div>
        )}

        <button className="pd-fix-btn" onClick={handleFixPortionSizes} disabled={isApplyingFix}>
          <Scale style={{ width: 16, height: 16 }} />
          {isApplyingFix
            ? 'Applying...'
            : selectedDish !== null
              ? `Fix Portion: ${dishes[selectedDish]?.name || 'Selected Dish'}`
              : 'Fix Portion Sizes (Select Dish)'}
        </button>

        {popupStep && (
          <div className="abc-modal-overlay" onClick={closePopup}>
            <div className="abc-modal" onClick={(e) => e.stopPropagation()}>
              {popupStep === 'input' && pendingDish && (
                <>
                  <div className="abc-modal-header">
                    <h3 className="abc-modal-title">Update Portion Mapping</h3>
                    <button className="abc-modal-close" onClick={closePopup} aria-label="Close">
                      ×
                    </button>
                  </div>
                  <div className="abc-modal-content" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                      <label style={{ fontSize: '0.82rem', color: 'hsl(var(--muted-foreground))' }}>Target Dish</label>
                      <select
                        value={pendingDish.menuItemId}
                        onChange={(e) => handleTargetDishChange(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '0.55rem 0.65rem',
                          borderRadius: '8px',
                          border: '1px solid hsl(var(--border))',
                          fontSize: '0.92rem',
                          background: 'hsl(var(--background))',
                        }}
                      >
                        {dishes
                          .filter((d) => hasMeasurableDrift(d) && d?.menuItemId)
                          .map((d) => (
                            <option key={d.menuItemId} value={d.menuItemId}>{d.name}</option>
                          ))}
                      </select>
                    </div>
                    <p style={{ fontSize: '0.9rem' }}>
                      Calculated new mapped quantity for <strong>{pendingDish.name}</strong>:
                    </p>
                    <p style={{ fontSize: '0.82rem', color: 'hsl(var(--muted-foreground))' }}>
                      Based on audit calculation: mapped quantity + per-plate drift = actual serving quantity.
                    </p>
                    {isManualQuantityRequired && (
                      <p style={{ fontSize: '0.82rem', color: 'hsl(var(--warning))' }}>
                        Auto-calculated quantity is unavailable for this dish in the current window (inferred actual usage is zero/invalid). Please enter the corrected mapped quantity manually.
                      </p>
                    )}
                    {shouldForceKg && (
                      <p style={{ fontSize: '0.78rem', color: 'hsl(var(--muted-foreground))' }}>
                        Quantity is auto-shown in kg because it is 1000g or more.
                      </p>
                    )}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 92px', gap: '0.5rem' }}>
                      <input
                        type="number"
                        min="0.001"
                        step="0.001"
                        value={quantityInput}
                        onChange={(e) => {
                          const nextValue = e.target.value;
                          setQuantityInput(nextValue);
                          const parsed = parseFloat(nextValue);
                          if (!Number.isFinite(parsed) || parsed <= 0) {
                            setCalculatedBaseQuantity(0);
                            return;
                          }
                          setCalculatedBaseQuantity(fromAnchor(toAnchor(parsed, selectedUnit), baseUnit));
                        }}
                        aria-label="Calculated quantity"
                        style={{
                          width: '100%',
                          padding: '0.65rem 0.75rem',
                          borderRadius: '8px',
                          border: '1px solid hsl(var(--border))',
                          fontSize: '0.95rem',
                          background: 'hsl(var(--muted) / 0.35)',
                        }}
                      />
                      <select
                        value={selectedUnit}
                        onChange={(e) => handleUnitChange(e.target.value)}
                        aria-label="Unit"
                        disabled={shouldForceKg}
                        style={{
                          width: '100%',
                          padding: '0.65rem 0.5rem',
                          borderRadius: '8px',
                          border: '1px solid hsl(var(--border))',
                          fontSize: '0.95rem',
                          textAlign: 'center',
                          background: 'hsl(var(--background))',
                          fontWeight: 600,
                          opacity: shouldForceKg ? 0.7 : 1,
                        }}
                      >
                        {effectiveUnitOptions.map((u) => (
                          <option key={u} value={u}>{u}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="abc-modal-footer" style={{ justifyContent: 'flex-end', gap: '0.5rem' }}>
                    <button className="abc-modal-footer-btn" onClick={closePopup}>Cancel</button>
                    <button className="abc-modal-footer-btn primary" onClick={handleInputNext}>Next</button>
                  </div>
                </>
              )}

              {popupStep === 'confirm-1' && pendingDish && (
                <>
                  <div className="abc-modal-header">
                    <h3 className="abc-modal-title">Confirm Portion Update</h3>
                    <button className="abc-modal-close" onClick={closePopup} aria-label="Close">×</button>
                  </div>
                  <div className="abc-modal-content">
                    Update <strong>{pendingDish.name}</strong> mapping from <strong>{pendingDish.mappedGrams}{baseUnit}</strong> to <strong>{quantityInput}{selectedUnit}</strong>?
                  </div>
                  <div className="abc-modal-footer" style={{ justifyContent: 'flex-end', gap: '0.5rem' }}>
                    <button className="abc-modal-footer-btn" onClick={closePopup}>Cancel</button>
                    <button className="abc-modal-footer-btn primary" onClick={() => setPopupStep('confirm-2')}>Confirm</button>
                  </div>
                </>
              )}

              {popupStep === 'confirm-2' && pendingDish && (
                <>
                  <div className="abc-modal-header">
                    <h3 className="abc-modal-title">Final Confirmation</h3>
                    <button className="abc-modal-close" onClick={closePopup} aria-label="Close">×</button>
                  </div>
                  <div className="abc-modal-content">
                    This will permanently change item mapping for this dish and ingredient. Continue?
                  </div>
                  <div className="abc-modal-footer" style={{ justifyContent: 'flex-end', gap: '0.5rem' }}>
                    <button className="abc-modal-footer-btn" onClick={closePopup}>Cancel</button>
                    <button className="abc-modal-footer-btn primary" onClick={handleFinalApply} disabled={isApplyingFix}>
                      {isApplyingFix ? 'Applying...' : 'Apply Fix'}
                    </button>
                  </div>
                </>
              )}

              {popupStep === 'result' && (
                <>
                  <div className="abc-modal-header">
                    <h3 className="abc-modal-title">{resultPopup.title}</h3>
                    <button className="abc-modal-close" onClick={closePopup} aria-label="Close">×</button>
                  </div>
                  <div className="abc-modal-content">{resultPopup.message}</div>
                  <div className="abc-modal-footer" style={{ justifyContent: 'flex-end' }}>
                    <button className="abc-modal-footer-btn primary" onClick={closePopup}>OK</button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const OpportunityLossDetailed = ({ leakGrams, dishes, worstDish, worstLossAmount }) => {
  const [selectedDish, setSelectedDish] = useState(null);
  const [viewMode, setViewMode] = useState('money');
  const maxLoss = dishes.length > 0 ? Math.max(0, ...dishes.map((d) => Number(d.lostRevenue || 0))) : 0;

  return (
    <div
      className="glass-card ol-card"
      style={{ padding: '1rem', width: '100%', maxWidth: '600px', minHeight: '275px', display: 'flex', flexDirection: 'column' }}
    >
      <div className="ol-bg-blob" />
      <div className="ol-inner" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div className="ol-header">
          <div>
            <h3 className="ol-title">Opportunity Loss</h3>
          </div>

          <div className="ol-mode-toggle">
            {['money', 'plates', 'grams'].map((mode) => (
              <button key={mode} className={`ol-mode-btn ${viewMode === mode ? 'active' : ''}`} onClick={() => setViewMode(mode)}>
                {mode === 'money' ? '₹ Revenue' : mode === 'plates' ? '🍽 Plates' : '⚖ Grams'}
              </button>
            ))}
          </div>
        </div>

        {dishes.length === 0 ? (
          <div style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid hsl(var(--border))',
            borderRadius: '8px',
            marginBottom: '1rem',
            color: 'hsl(var(--muted-foreground))',
            fontSize: '0.9rem',
            minHeight: '100px'
          }}>
            No batches recorded
          </div>
        ) : (
          <div className="ol-dishes">
            {dishes.map((dish, i) => {
              const isSelected = selectedDish === i;
              const safeLostRevenue = Number(dish.lostRevenue || 0);
              const lossBarWidth = maxLoss > 0 ? (safeLostRevenue / maxLoss) * 100 : 0;
              const isMax = safeLostRevenue === maxLoss && maxLoss > 0;
              const dishPlatesCanMake = Number(dish.platesCanBeMade || 0);
              const dishPlatesSold   = Number(dish.platesSold || dish.platesSold !== undefined ? dish.platesSold : 0);
              const displayValue =
                viewMode === 'money' ? `₹${safeLostRevenue.toLocaleString()}` :
                viewMode === 'plates' ? `${dish.lostPlates} plates` :
                `${(Number(dish.lostPlates || 0) * Number(dish.portionGrams || 0)).toLocaleString()}g`;

              return (
                <div
                  key={dish.name}
                  className={`ol-dish ${isSelected ? 'selected' : ''}`}
                  onClick={() => setSelectedDish(isSelected ? null : i)}
                  style={{ animationDelay: `${i * 0.1}s` }}
                >
                  <div className="ol-dish-bar" style={{ width: `${lossBarWidth}%` }} />
                  <div className="ol-dish-content">
                    <div className="ol-dish-row">
                      <div className="ol-dish-left">
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <p className="ol-dish-name">{dish.name}</p>
                            {isMax && <span className="ol-highest-badge">Highest</span>}
                          </div>
                          <p className="ol-dish-calc">
                            {dishPlatesCanMake > 0
                              ? `Can make ${dishPlatesCanMake} plates, sold ${dishPlatesSold} → ${dish.lostPlates} leaked`
                              : `${leakGrams.toLocaleString()}g ÷ ${dish.portionGrams}g = ${dish.lostPlates} plates lost`}
                          </p>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span className="ol-dish-value">{displayValue}</span>
                        <ChevronRight className="ol-dish-chevron" style={{ width: 16, height: 16 }} />
                      </div>
                    </div>
                  </div>

                  <div className={`ol-dish-expanded ${isSelected ? 'open' : ''}`}>
                    <div className="ol-dish-expanded-inner">
                      <div className="ol-detail-grid">
                        <div className="ol-detail-item">
                          <p className="ol-detail-label">Leak Plates</p>
                          <p className="ol-detail-value color-danger">{dish.lostPlates}</p>
                        </div>
                        <div className="ol-detail-item">
                          <p className="ol-detail-label">Per Plate</p>
                          <p className="ol-detail-value color-foreground">₹{dish.sellingPrice}</p>
                        </div>
                        <div className="ol-detail-item">
                          <p className="ol-detail-label">Lost Revenue</p>
                          <p className="ol-detail-value color-danger">₹{safeLostRevenue.toLocaleString()}</p>
                        </div>
                      </div>
                      <p className="ol-detail-text">
                        {dish.lostPlates} plates × ₹{dish.sellingPrice} ={' '}
                        <span className="mono color-danger" style={{ fontWeight: 700 }}>
                          ₹{safeLostRevenue.toLocaleString()}
                        </span>{' '}
                      lost revenue
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
          </div>
        )}

        <div className="ol-verdict">
          <div>
            <p className="ol-verdict-title">Financial Verdict</p>
            <p className="ol-verdict-text">
              You lost up to <span className="mono color-danger" style={{ fontWeight: 700 }}>₹{worstLossAmount.toLocaleString()}</span> in this period because the kitchen is
              over-portioning <span style={{ fontWeight: 600, color: 'hsl(var(--foreground))' }}>{worstDish}</span>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const ABCAnalysisModal = ({ data, onClose }) => {
  const [activeCategory, setActiveCategory] = useState('A');

  return (
    <div className="leakage-page">
      <div className="dashboard-container">
        <header className="leakage-screen-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '1px' }}>
            <button className="leakage-back-btn" onClick={onClose} aria-label="Back">
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
            <h1 className="leakage-page-title">Advanced Features</h1>
          </div>
        </header>

        <div className="abc-page-inner">
          <div className="abc-modal-tabs">
            {['A', 'B', 'C'].map(cat => (
              <button
                key={cat}
                className={`abc-tab ${activeCategory === cat ? 'active' : ''}`}
                onClick={() => setActiveCategory(cat)}
              >
                <span className={`abc-badge abc-${cat.toLowerCase()}`}>{cat}</span>
              </button>
            ))}
          </div>

          <div className="abc-page-content">
            <div className="abc-category-description">
              {activeCategory === 'A' && (
                <p><strong>Category A:</strong> High-value items requiring tight monitoring (80% of consumption)</p>
              )}
              {activeCategory === 'B' && (
                <p><strong>Category B:</strong> Medium-value items with moderate monitoring (15% of consumption)</p>
              )}
              {activeCategory === 'C' && (
                <p><strong>Category C:</strong> Low-value items with basic monitoring (5% of consumption)</p>
              )}
            </div>

            <div className="abc-items-list">
              {(data.abcCategories[activeCategory] || []).map((item, index) => (
                <div key={item.item_id} className="abc-item" style={{ animationDelay: `${index * 0.05}s` }}>
                  <div className="abc-item-left">
                    <div>
                      <div className="abc-item-header-row">
                        <span className="abc-item-name">{item.name}</span>
                        <span className={`status-${item.stock_status}`}>{item.stock_status}</span>
                      </div>
                      <div className="abc-item-details-inline">
                        <span className="abc-detail-item abc-stock-detail">
                          <span className="abc-detail-bullet">•</span> Available Stock: {parseFloat(item.current_stock || 0).toFixed(1)} {item.unit_symbol}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="abc-item-right">
                    <p className="abc-item-value">₹{parseFloat(item.consumption_value || 0).toLocaleString()}</p>
                    <p className="abc-item-sublabel">Consumption Value</p>
                  </div>
                </div>
              ))}
              
              {(!data.abcCategories[activeCategory] || data.abcCategories[activeCategory].length === 0) && (
                <p style={{ textAlign: 'center', padding: '2rem', color: 'hsl(var(--muted-foreground))' }}>
                  No items in Category {activeCategory}
                </p>
              )}
            </div>
          </div>

          <div className="abc-page-footer">
            <p className="abc-modal-footer-text">
              Total items analyzed: <strong>{data.total}</strong>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const SectionLabel = ({ step, title, subtitle }) => (
  <div className="section-label">
    <div className="section-step">{step}</div>
    <div>
      <h2 className="section-title">{title}</h2>
      <p className="section-subtitle">{subtitle}</p>
    </div>
  </div>
);

const PopupMessageModal = ({ open, title, message, onClose }) => {
  if (!open) return null;

  return (
    <div className="abc-modal-overlay" onClick={onClose}>
      <div className="abc-modal error-popup-design" onClick={(e) => e.stopPropagation()} style={{
        padding: '26px 22px',
        textAlign: 'center',
        borderRadius: '16px',
        maxWidth: '380px',
        width: '88%',
        boxShadow: '0 20px 50px rgba(0,0,0,0.22)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#ffffff',
        border: '1px solid #e5e7eb',
        boxSizing: 'border-box'
      }}>
        <div style={{
          width: '52px',
          height: '52px',
          borderRadius: '50%',
          backgroundColor: '#fee2e2',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '16px',
          flexShrink: 0
        }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </div>
        <div style={{ 
          fontSize: '14.5px', 
          fontWeight: '500', 
          color: '#1f2937', 
          marginBottom: '20px',
          lineHeight: '1.5',
          textAlign: 'center',
          padding: '0 4px'
        }}>
          {message}
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
          <button style={{
            backgroundColor: '#0b0b0b',
            color: '#ffffff',
            border: 'none',
            borderRadius: '8px',
            padding: '10px 36px',
            fontSize: '14.5px',
            fontWeight: '600',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            minWidth: '120px',
            transition: 'all 0.2s ease'
          }} onClick={onClose}>
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

const Leakage = ({ goTo, ingredientId, ingredientName }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [auditData, setAuditData] = useState(null);
  const [ingredients, setIngredients] = useState([]);
  const [selectedIngredient, setSelectedIngredient] = useState(ingredientId || null);
  const [showABCAnalysis, setShowABCAnalysis] = useState(false);
  const [abcData, setAbcData] = useState(null);
  const [loadingABC, setLoadingABC] = useState(false);
  const [popupMessage, setPopupMessage] = useState({ open: false, title: '', message: '' });
  const latestAuditRequestRef = React.useRef(0);
  const previousSelectedIngredientRef = React.useRef(null);
  const showPopupMessage = useCallback((title, message) => {
    setPopupMessage({ open: true, title, message });
  }, []);

  const closePopupMessage = useCallback(() => {
    setPopupMessage((prev) => ({ ...prev, open: false }));
  }, []);
  
  // Closing date drives window end; start comes from first TSR (first cycle)
  // or previous closing date (continuation cycle).
  const [selectedClosingDate, setSelectedClosingDate] = useState(() => resolveInitialClosingDate());
  const [previousClosingDate, setPreviousClosingDate] = useState(null);

  const businessId = useMemo(() => {
    const rawBusinessId = localStorage.getItem('businessId');
    const parsedBusinessId = Number(rawBusinessId);
    return Number.isFinite(parsedBusinessId) && parsedBusinessId > 0 ? parsedBusinessId : null;
  }, []);
  const hasBusinessContext = businessId !== null;

  useEffect(() => {
    const normalizedClosingDate = String(selectedClosingDate || '').trim();
    if (!isValidISODateString(normalizedClosingDate)) {
      return;
    }

    try {
      localStorage.setItem('weeklyAudit.selectedClosingDate', normalizedClosingDate);
      if (hasBusinessContext) {
        localStorage.setItem(`weeklyAudit.selectedClosingDate_${businessId}`, normalizedClosingDate);
      }
    } catch (storageError) {
      // non-fatal: leakage calculations do not depend on storage availability
    }
  }, [selectedClosingDate, hasBusinessContext, businessId]);

  useEffect(() => {
    if (!ingredientId) return;
    const parsedIngredientId = Number(ingredientId);
    if (!Number.isFinite(parsedIngredientId) || parsedIngredientId <= 0) return;
    setSelectedIngredient((prev) => (prev === parsedIngredientId ? prev : parsedIngredientId));
  }, [ingredientId]);

  useEffect(() => {
    console.log('🔍 [LEAKAGE] Component initialized with businessId:', businessId, 'ingredientId:', ingredientId, 'ingredientName:', ingredientName);
    if (!hasBusinessContext) {
      console.warn('⚠️ [LEAKAGE] Missing businessId in localStorage. Leakage data fetch is blocked.');
    }
    console.log('🎨 [LEAKAGE] Ingredient selector enabled in header.');
  }, [businessId, hasBusinessContext, ingredientId, ingredientName]);
  
  // Resolve API host while keeping endpoints under /api.
  // If env vars are missing in production, use same-origin (/api/...).
  const API_BASE_URL = useMemo(() => {
    const raw = (process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || '').trim();
    if (!raw) return '';
    return raw.replace(/\/+$/, '').replace(/\/api$/, '');
  }, []);

  // Fetch available ingredients on mount.
  // Keep list available even when launched with a pre-selected ingredient,
  // so operator can switch to other items from leakage itself.
  useEffect(() => {
    let isMounted = true;
    if (!hasBusinessContext) {
      setError('Business context missing. Please login again.');
      setLoading(false);
      return () => {
        isMounted = false;
      };
    }

    const cacheKey = String(businessId);
    
    const applyIngredientList = (ingredientList) => {
      if (!isMounted) return;

      console.log('✅ [LEAKAGE] Ingredients loaded:', {
        count: ingredientList.length,
        first3: ingredientList.slice(0, 3).map(i => ({ id: i.itemId, name: i.name }))
      });

      setIngredients(ingredientList);

      if (ingredientList.length > 0) {
        const requestedName = normalizeIngredientLabel(ingredientName);
        const requestedId = Number(ingredientId);

        const matchByName = requestedName
          ? ingredientList.find((ing) => {
              const ingName = normalizeIngredientLabel(ing.name);
              return ingName === requestedName || ingName.includes(requestedName) || requestedName.includes(ingName);
            })
          : null;

        const matchById = Number.isFinite(requestedId) && requestedId > 0
          ? ingredientList.find((ing) => Number(ing.itemId) === requestedId)
          : null;

        const requestedIngredient = matchByName || matchById || null;

        setSelectedIngredient((prev) => {
          if (requestedIngredient?.itemId) {
            console.log('🎯 [LEAKAGE] Using requested ingredient from XYZ:', {
              requestedName: ingredientName,
              requestedId: ingredientId,
              resolvedItemId: requestedIngredient.itemId,
              resolvedName: requestedIngredient.name,
            });
            return requestedIngredient.itemId;
          }

          if (prev) {
            // Preserve externally selected ingredient (from XYZ click) even if it is not in the quick list.
            return prev;
          }

          console.log('🎯 [LEAKAGE] Auto-selecting first ingredient:', ingredientList[0].name);
          return ingredientList[0].itemId;
        });
      }

      if (ingredientList.length === 0) {
        console.log('⚠️  [LEAKAGE] No ingredients with batches available');
        setError('No ingredients with inventory batches available. Please add inventory batches first.');
        setLoading(false);
      }
    };

    const cachedIngredients = leakageIngredientsCache.get(cacheKey);
    if (cachedIngredients) {
      console.log('📦 [LEAKAGE] Using cached ingredients for businessId:', businessId);
      applyIngredientList(cachedIngredients);
      return () => {
        isMounted = false;
      };
    }
    
    const loadIngredients = async () => {
      try {
        console.log('🔍 [LEAKAGE] Fetching ingredients for businessId:', businessId);

        let request = leakageIngredientsInFlight.get(cacheKey);
        if (!request) {
          request = axios.get(`${API_BASE_URL}/api/weekly-audit/ingredients`, {
            params: { businessId },
            timeout: 10000 // 10 second timeout
          });
          leakageIngredientsInFlight.set(cacheKey, request);
        }

        const response = await request;
        const ingredientList = response.data.ingredients || [];
        leakageIngredientsCache.set(cacheKey, ingredientList);
        applyIngredientList(ingredientList);
      } catch (err) {
        console.error('❌ [LEAKAGE] Error fetching ingredients:', err);
        if (isMounted) {
          const apiCode = err?.response?.data?.code;
          const errorMsg = getApiErrorMessage(err, 'Failed to load ingredients. Please check your connection.');
          if (apiCode === WEEKLY_AUDIT_DISABLED_CODE) {
            leakageIngredientsCache.delete(cacheKey);
            setIngredients([]);
            setAuditData(null);
          }
          setError(errorMsg);
          setLoading(false);
        }
      } finally {
        leakageIngredientsInFlight.delete(cacheKey);
      }
    };

    loadIngredients();

    return () => {
      isMounted = false;
    };
  }, [API_BASE_URL, businessId, hasBusinessContext, ingredientId, ingredientName]);

  useEffect(() => {
    if (!selectedIngredient) return;

    if (
      previousSelectedIngredientRef.current !== null
      && previousSelectedIngredientRef.current !== selectedIngredient
    ) {
      // Prevent previous ingredient data (e.g., Oil) from persisting while switching to a new selection.
      setAuditData(null);
    }

    previousSelectedIngredientRef.current = selectedIngredient;
  }, [selectedIngredient]);

  // Fetch audit data when ingredient selection or closing anchors change.
  useEffect(() => {
    const effectiveItemId = selectedIngredient;
    if (effectiveItemId) {
      fetchAuditData(effectiveItemId, selectedClosingDate, previousClosingDate);
    }
  }, [selectedIngredient, selectedClosingDate, previousClosingDate]);

  const fetchAuditData = useCallback(async (itemId, closingDate = null, prevClosingDate = null) => {
    if (!itemId) return;
    if (!hasBusinessContext) {
      setError('Business context missing. Please login again.');
      return;
    }

    const requestId = latestAuditRequestRef.current + 1;
    latestAuditRequestRef.current = requestId;
    
    const params = { businessId, itemId };
    if (closingDate) params.closingDate = closingDate;
    if (prevClosingDate) params.previousClosingDate = prevClosingDate;
    
    console.log('🔍 [LEAKAGE] Fetching audit data:', params);
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${API_BASE_URL}/api/weekly-audit/calculate`, {
        params,
        timeout: 15000 // 15 second timeout
      });

      if (requestId !== latestAuditRequestRef.current) {
        return;
      }

      const effectiveClosingDate = response.data?.closingDate || null;
      if (effectiveClosingDate) {
        setSelectedClosingDate((prev) => (prev !== effectiveClosingDate ? effectiveClosingDate : prev));
      }
      if (Object.prototype.hasOwnProperty.call(response.data || {}, 'previousClosingDate')) {
        const effectivePreviousClosingDate = response.data?.previousClosingDate || null;
        setPreviousClosingDate((prev) => (prev !== effectivePreviousClosingDate ? effectivePreviousClosingDate : prev));
      }

      console.log('✅ [LEAKAGE] Audit data received:', {
        weekNumber: response.data.weekNumber,
        ingredient: response.data.ingredient,
        batchCount: response.data.masterBatch?.batches?.length || 0,
        salesCount: response.data.consumption?.sales?.length || 0,
        totalPlates: response.data.summary?.totalPlatesSold || 0
      });
      
      // Check if no batches found or closing date adjusted
      const batchCount = response.data.masterBatch?.batches?.length || 0;
      if (response.data?.closingDateAdjusted) {
        const reqDate = formatDateToDDMMYYYY(response.data.requestedClosingDate || selectedClosingDate);
        const confDate = formatDateToDDMMYYYY(response.data.closingDate || selectedClosingDate);
        showPopupMessage("Error", `Closing stock is not completed for ${reqDate}, so this report is anchored to the last confirmed closing date ${confDate}.`);
      } else if (batchCount === 0) {
        console.warn('⚠️  [LEAKAGE] NO BATCHES FOUND for this ingredient in selected period!');
        const windowStartDate = formatDateToDDMMYYYY(response.data.startDate);
        const windowEndDate = formatDateToDDMMYYYY(response.data.endDate);
        const isB102AprilReset = response.data.windowStartMode === 'b102_april_reset';
        const isContinuationCycle = response.data.cycleMode === 'continuation_cycle';
        const label = isB102AprilReset
          ? `between April 1 opening stock and selected closing date (${windowEndDate}). Effective window: ${windowStartDate} to ${windowEndDate}.`
          : (isContinuationCycle
            ? `between previous closing (${formatDateToDDMMYYYY(response.data.previousClosingDate)}) and selected closing date (${windowEndDate}). Effective window: ${windowStartDate} to ${windowEndDate}.`
            : `between first TSR and selected closing date (${windowEndDate}). Effective window: ${windowStartDate} to ${windowEndDate}.`);
        showPopupMessage("Error", `No stock-in batches found ${label}`);
      }
      
      setAuditData(response.data);
    } catch (err) {
      if (requestId !== latestAuditRequestRef.current) {
        return;
      }
      console.error('❌ [LEAKAGE] Error fetching audit data:', err);
      const apiCode = err?.response?.data?.code;
      const errorMsg = getApiErrorMessage(err, 'Failed to load audit data');
      if (apiCode === WEEKLY_AUDIT_DISABLED_CODE) {
        setAuditData(null);
      }
      setError(errorMsg);
    } finally {
      if (requestId === latestAuditRequestRef.current) {
        setLoading(false);
      }
    }
  }, [API_BASE_URL, businessId, hasBusinessContext, showPopupMessage]);

  const shiftDateByDays = useCallback((dateStr, days) => {
    const base = new Date(`${dateStr}T00:00:00`);
    if (Number.isNaN(base.getTime())) return dateStr;
    base.setDate(base.getDate() + days);
    const y = base.getFullYear();
    const m = String(base.getMonth() + 1).padStart(2, '0');
    const d = String(base.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }, []);

  // Navigate to previous confirmed closing cycle.
  const goToPreviousWeek = useCallback(() => {
    const previousConfirmedClosingDate = auditData?.availablePreviousClosingDate;
    if (previousConfirmedClosingDate && previousConfirmedClosingDate !== selectedClosingDate) {
      // Moving backward resets to the earlier cycle endpoint.
      setPreviousClosingDate(null);
      setSelectedClosingDate(previousConfirmedClosingDate);
      return;
    }

    const prevDate = shiftDateByDays(selectedClosingDate, -7);
    setPreviousClosingDate(null);
    setSelectedClosingDate(prevDate);
  }, [auditData, selectedClosingDate, shiftDateByDays]);

  // Navigate to next confirmed closing cycle.
  const goToNextWeek = useCallback(() => {
    const nextConfirmedClosingDate = auditData?.availableNextClosingDate;
    if (nextConfirmedClosingDate && nextConfirmedClosingDate !== selectedClosingDate) {
      // Keep first-TSR mode when switching closing date.
      setPreviousClosingDate(null);
      setSelectedClosingDate(nextConfirmedClosingDate);
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const next = shiftDateByDays(selectedClosingDate, 7);
    const nextObj = new Date(`${next}T00:00:00`);
    if (Number.isNaN(nextObj.getTime())) return;
    let nextDate = next;
    if (nextObj > today) {
      nextDate = getTodayDateString();
    }
    setPreviousClosingDate(null);
    setSelectedClosingDate(nextDate);
  }, [auditData, selectedClosingDate, shiftDateByDays]);

  // Reset to today's closing date.
  const goToCurrentWeek = useCallback(() => {
    const today = getTodayDateString();
    setPreviousClosingDate(null);
    setSelectedClosingDate(today);
  }, []);

  const fetchABCAnalysis = useCallback(async () => {
    if (!hasBusinessContext) {
      showPopupMessage('Business Not Found', 'Please login again to load business data.');
      return;
    }

    setLoadingABC(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/weekly-audit/abc-integration`, {
        params: { businessId },
        timeout: 10000
      });
      setAbcData(response.data);
      setShowABCAnalysis(true);
    } catch (err) {
      console.error('Error fetching ABC analysis:', err);
      const apiCode = err?.response?.data?.code;
      const errorMsg = getApiErrorMessage(err, 'Failed to load ABC analysis. Please try again.');
      if (apiCode === WEEKLY_AUDIT_DISABLED_CODE) {
        setShowABCAnalysis(false);
        setAbcData(null);
      }
      showPopupMessage('ABC Analysis Unavailable', errorMsg);
    } finally {
      setLoadingABC(false);
    }
  }, [API_BASE_URL, businessId, hasBusinessContext, showPopupMessage]);

  const handleBack = useCallback(() => {
    if (typeof goTo === 'function') {
      goTo('xyz');
      return;
    }
    window.history.back();
  }, [goTo]);

  const selectedIngredientData = useMemo(() => {
    return ingredients.find(ing => ing.itemId === selectedIngredient);
  }, [ingredients, selectedIngredient]);

  // Show loading state
  if (loading && !auditData) {
    return (
      <div className="leakage-page">
        <div className="dashboard-container">
          <div className="leakage-loading-state">
            <div className="leakage-loading-brand">
              <img
                src="/invexis-logo.png"
                alt="Invexis"
                className="leakage-loading-logo"
              />
              <div className="leakage-loading-ring"></div>
            </div>
            <p className="leakage-loading-text">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error && !auditData) {
    return (
      <div className="leakage-page">
        <div className="dashboard-container">
          <header className="leakage-screen-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '1px' }}>
              <button className="leakage-back-btn" onClick={handleBack} aria-label="Back">
                <FontAwesomeIcon icon={faArrowLeft} />
              </button>
              <h1 className="leakage-page-title">Leakage Page</h1>
            </div>
          </header>
          <div className="dashboard-inner">
            <div className="error-state" style={{ 
              padding: '3rem 2rem', 
              textAlign: 'center',
              background: 'hsl(var(--card))',
              borderRadius: '12px',
              border: '1px solid hsl(var(--border))'
            }}>
              <AlertTriangle style={{ width: 48, height: 48, color: 'hsl(var(--danger))', margin: '0 auto 1rem' }} />
              <h3 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.5rem' }}>
                Error Loading Audit Data
              </h3>
              <p style={{ color: 'hsl(var(--muted-foreground))', marginBottom: '1.5rem' }}>
                {error}
              </p>
              <button 
                onClick={() => ingredients.length > 0 && fetchAuditData(ingredients[0].itemId)}
                style={{
                  padding: '0.5rem 1.5rem',
                  borderRadius: '8px',
                  border: '1px solid hsl(var(--primary))',
                  background: 'hsl(var(--primary))',
                  color: 'white',
                  cursor: 'pointer',
                  fontWeight: 600
                }}
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show no data message
  if (!auditData && !loading) {
    return (
      <div className="leakage-page">
        <div className="dashboard-container">
          <header className="leakage-screen-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '1px' }}>
              <button className="leakage-back-btn" onClick={handleBack} aria-label="Back">
                <FontAwesomeIcon icon={faArrowLeft} />
              </button>
              <h1 className="leakage-page-title">Leakage Page</h1>
            </div>
          </header>
          <div className="dashboard-inner">
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              <Package style={{ width: 48, height: 48, color: 'hsl(var(--muted-foreground))', margin: '0 auto 1rem' }} />
              <p>No audit data available. Please select an ingredient.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Safely destructure with defaults
  const { 
    summary = { 
      efficiency: 0, 
      totalPlatesSold: 0, 
      platesCanBeMade: 0,
      leakPlates: 0,
      leakKg: 0, 
      financialLoss: 0, 
      rootCause: '', 
      message: '' 
    }, 
    masterBatch = { 
      batches: [], 
      totalKg: 0, 
      totalValue: 0, 
      weightedAvgCost: 0 
    }, 
    consumption = { 
      masterBatchGrams: 0, 
      accountedGrams: 0, 
      salesGrams: 0, 
      actualUsageGrams: 0,
      wasteGrams: 0, 
      leakGrams: 0, 
      leakKg: 0, 
      leakValuePerKg: 0, 
      wasteKg: 0, 
      carryForwardGrams: 0,
      carryForwardKg: 0,
      sales: [] 
    }, 
    portionDrift = { 
      dishes: [], 
      highestDrift: { dish: '', extraGrams: 0 } 
    }, 
    opportunityLoss = { 
      dishes: [], 
      worstCase: { 
        dish: '', 
        lostPlates: 0, 
        lostRevenue: 0 
      } 
    }, 
    weekNumber = 24,
    ingredient = 'Unknown',
    dataIntegrity = []
  } = auditData || {};

  const dataIntegrityIssues = Array.isArray(dataIntegrity) ? dataIntegrity : [];

  const displayIngredientNameRaw = selectedIngredientData?.name || ingredient || ingredientName || '';
  const displayIngredientName = displayIngredientNameRaw 
    ? displayIngredientNameRaw.charAt(0).toUpperCase() + displayIngredientNameRaw.slice(1) 
    : '';
  const displayUnitSymbol = selectedIngredientData?.unitSymbol || auditData?.unitSymbol || 'g';
  const currentAuditItemId = selectedIngredient || auditData?.itemId;
  const isHistoricalWeekView = false;
  const isCurrentClosingDate = selectedClosingDate === getTodayDateString();
  const isContinuationCycle = auditData?.cycleMode === 'continuation_cycle';
  const isB102AprilReset = auditData?.windowStartMode === 'b102_april_reset';
  const windowStartDate = formatDateToDDMMYYYY(auditData?.startDate);
  const windowEndDate = formatDateToDDMMYYYY(auditData?.endDate);
  const formattedPrevClosing = formatDateToDDMMYYYY(auditData?.previousClosingDate);
  const noBatchWindowLabel = isB102AprilReset
    ? `between April 1 opening stock and selected closing date (${windowEndDate}). Effective window: ${windowStartDate} to ${windowEndDate}.`
    : (isContinuationCycle
      ? `between previous closing (${formattedPrevClosing}) and selected closing date (${windowEndDate}). Effective window: ${windowStartDate} to ${windowEndDate}.`
      : `between first TSR and selected closing date (${windowEndDate}). Effective window: ${windowStartDate} to ${windowEndDate}.`);
  const modeBannerText = `Batch-wise cycle from ${windowStartDate} to ${windowEndDate}`;
  const closingDateAdjustmentNotice = auditData?.closingDateAdjusted
    ? `Closing stock is not completed for ${auditData?.requestedClosingDate || selectedClosingDate}, so this report is anchored to the last confirmed closing date ${auditData?.closingDate || selectedClosingDate}.`
    : null;

  const handleApplyFixPortion = async ({ targetDish, newQuantity, unitSymbol }) => {
    const unitLabel = unitSymbol || displayUnitSymbol || 'g';
    if (!hasBusinessContext) {
      return {
        ok: false,
        message: 'Business context missing. Please login again.',
      };
    }

    if (!currentAuditItemId || !targetDish?.menuItemId) {
      return {
        ok: false,
        message: 'Missing mapping context. Please refresh and try again.',
      };
    }

    try {
      const payload = {
        businessId,
        menuItemId: targetDish.menuItemId,
        itemId: currentAuditItemId,
        // newQuantity is expressed in the ingredient BASE unit (g/ml); send the
        // unit explicitly so the backend never has to guess (Bug 1 / Unit Contract).
        newQuantity,
        baseUnit: unitSymbol || displayUnitSymbol || 'g',
      };

      const response = await axios.post(`${API_BASE_URL}/api/weekly-audit/portion-fix`, payload, {
        timeout: 15000,
      });

      const updated = response?.data?.data;
      await fetchAuditData(currentAuditItemId, selectedClosingDate, previousClosingDate);

      return {
        ok: true,
        message: `Portion mapping updated: ${targetDish.name} (${updated?.oldQuantity ?? targetDish.mappedGrams}${unitLabel} -> ${updated?.newQuantity ?? newQuantity}${unitLabel}).`,
      };
    } catch (err) {
      console.error('[LEAKAGE][PORTION FIX] Error:', err);
      const apiCode = err?.response?.data?.code;
      const message = getApiErrorMessage(err, 'Failed to update portion mapping.');
      if (apiCode === WEEKLY_AUDIT_DISABLED_CODE) {
        setAuditData(null);
      }
      return {
        ok: false,
        message: `Failed to update mapping: ${message}`,
      };
    }
  };

  // Show ABC Analysis as full page
  if (showABCAnalysis && abcData) {
    return (
      <ABCAnalysisModal 
        data={abcData} 
        onClose={() => setShowABCAnalysis(false)} 
      />
    );
  }

  return (
    <div className="leakage-page">
      <div className="dashboard-container">
        <header className="leakage-screen-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '1px' }}>
            <button className="leakage-back-btn" onClick={handleBack} aria-label="Back to XYZ">
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
            <h1 className="leakage-page-title">Leakage Page</h1>
          </div>

          <button 
            onClick={fetchABCAnalysis}
            disabled={loadingABC}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '8px',
              border: '1px solid hsl(var(--primary))',
              background: 'hsl(var(--primary))',
              color: 'white',
              cursor: loadingABC ? 'wait' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontWeight: 500,
              transition: 'all 0.2s',
              opacity: loadingABC ? 0.6 : 1
            }}
            onMouseEnter={(e) => !loadingABC && (e.target.style.opacity = '0.9')}
            onMouseLeave={(e) => !loadingABC && (e.target.style.opacity = '1')}
          >
            {loadingABC ? (
              <RefreshCw style={{ width: 16, height: 16, animation: 'spin 1s linear infinite' }} />
            ) : (
              <Sparkles style={{ width: 16, height: 16 }} />
            )}
            {loadingABC ? 'Loading...' : 'Advanced Features'}
          </button>
        </header>

        <div className="dashboard-inner">
          <PopupMessageModal
            open={popupMessage.open}
            title={popupMessage.title}
            message={popupMessage.message}
            onClose={closePopupMessage}
          />

          <div style={{
            marginBottom: '1.25rem',
            color: '#000000',
            fontWeight: 500,
            fontSize: '0.925rem',
            textAlign: 'center'
          }}>
            {modeBannerText}
          </div>

          {dataIntegrityIssues.length > 0 && (
            <div
              role="alert"
              style={{
                marginBottom: '1rem',
                padding: '0.75rem 1rem',
                borderRadius: '0.5rem',
                border: '1px solid hsl(var(--warning, 38 92% 50%))',
                background: 'hsla(38, 92%, 50%, 0.08)',
                color: '#7c4a03',
                fontSize: '0.875rem',
              }}
            >
              <strong>⚠️ This ingredient's data needs review.</strong>
              <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.25rem' }}>
                {dataIntegrityIssues.map((issue, i) => (
                  <li key={issue.code || i}>{issue.message || String(issue)}</li>
                ))}
              </ul>
              <div style={{ marginTop: '0.5rem', opacity: 0.85 }}>
                Leak figures are suppressed for this item until the data is corrected.
              </div>
            </div>
          )}

          <DashboardHeader
            onPreviousWeek={goToPreviousWeek}
            onNextWeek={goToNextWeek}
            onCurrentWeek={goToCurrentWeek}
            isCurrentWeek={isCurrentClosingDate}
          />

          {displayIngredientName && (
            <div style={{ marginTop: '0.25rem', marginBottom: '0.5rem', textAlign: 'left' }}>
              <span style={{ fontSize: '1.125rem', fontWeight: 600, color: 'hsl(var(--foreground))' }}>
                {displayIngredientName}
              </span>
            </div>
          )}

          <section className="section-block">
            <MasterBatch
              ingredient={displayIngredientName}
              batches={masterBatch.batches}
              totalKg={masterBatch.totalKg}
              totalValue={masterBatch.totalValue}
              weightedAvgCost={masterBatch.weightedAvgCost}
              isHistoricalWeek={isHistoricalWeekView}
              cycleMode={auditData?.cycleMode}
              windowStartMode={auditData?.windowStartMode}
              startDate={auditData?.startDate}
              endDate={auditData?.endDate}
              previousClosingDate={auditData?.previousClosingDate}
            />
          </section>

          {!isHistoricalWeekView && (
            <>
              {/* ── NEW PRIMARY SECTION: Plate Leakage Table ── */}
              <section className="section-block">
                <PlateLeakageTable
                  ingredient={displayIngredientName}
                  totalConsumedGrams={consumption.actualUsageGrams}
                  platesCanBeMade={summary.platesCanBeMade}
                  totalPlatesSold={summary.totalPlatesSold}
                  leakPlates={summary.leakPlates}
                  dishes={portionDrift.dishes}
                  unitSymbol={displayUnitSymbol}
                />
              </section>

              <section className="section-block">
                <BatchConsumptionHub
                  ingredient={displayIngredientName}
                  masterBatchGrams={consumption.masterBatchGrams}
                  activeBatchGrams={consumption.activeBatchGrams}
                  reservedStockGrams={consumption.reservedStockGrams}
                  accountedGrams={consumption.accountedGrams}
                  salesGrams={consumption.salesGrams}
                  actualUsageGrams={consumption.actualUsageGrams}
                  varianceBeforeWasteGrams={consumption.varianceBeforeWasteGrams}
                  wasteGrams={consumption.wasteGrams}
                  leakGrams={consumption.leakGrams}
                  leakKg={consumption.leakKg}
                  carryForwardGrams={consumption.carryForwardGrams}
                  carryForwardKg={consumption.carryForwardKg}
                  leakValuePerKg={consumption.leakValuePerKg}
                  wasteKg={consumption.wasteKg}
                  sales={consumption.sales}
                  unitSymbol={displayUnitSymbol}
                  serverEfficiency={summary.efficiency}
                />
              </section>

              <section className="section-block">
                <PortionDriftDetailed
                  leakGrams={consumption.leakGrams}
                  dishes={portionDrift.dishes}
                  ingredientItemId={currentAuditItemId}
                  unitSymbol={displayUnitSymbol}
                  onApplyFixPortion={handleApplyFixPortion}
                />
              </section>

              <section className="section-block">
                <OpportunityLossDetailed
                  leakGrams={consumption.leakGrams}
                  worstDish={opportunityLoss.worstCase.dish}
                  worstLossAmount={opportunityLoss.worstCase.lostRevenue}
                  dishes={opportunityLoss.dishes}
                />
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Leakage;
